import { useState, useEffect, useCallback } from 'react';
import { Users, Search, X, Loader2, Check } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { userService } from '@/services/user.service';
import toast from 'react-hot-toast';

interface PickableUser {
  _id: string;
  name: string;
  email: string;
}

interface UserPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventLabel: string;
  value: string[];
  onSave: (user_ids: string[]) => void;
}

const displayName = (user: any): string =>
  user?.full_name ||
  [user?.given_name, user?.family_name].filter(Boolean).join(' ') ||
  user?.name ||
  user?.email ||
  'Unknown user';

export function UserPickerModal({
  isOpen,
  onClose,
  eventLabel,
  value,
  onSave,
}: UserPickerModalProps) {
  const [users, setUsers] = useState<PickableUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Names for chips, including ids saved earlier that are not on this page.
  const [nameCache, setNameCache] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(value);
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, [isOpen, value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getUsers({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        status: 'ACTIVE',
      });

      const mapped: PickableUser[] = (response.data || []).map((user: any) => ({
        _id: user._id || user.id,
        name: displayName(user),
        email: user.email || '',
      }));

      setUsers(mapped);
      setTotal(response.total || mapped.length);

      setNameCache((prev) => {
        const next = { ...prev };
        for (const user of mapped) next[user._id] = user.name;
        return next;
      });
    } catch {
      toast.error('Failed to load users');
      setUsers([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    if (!isOpen) return;
    loadUsers();
  }, [isOpen, loadUsers]);

  // The list endpoint has no user_ids filter, so previously saved ids are
  // resolved one at a time and only when missing from the cache.
  useEffect(() => {
    if (!isOpen || value.length === 0) return;

    const missing = value.filter((id) => !nameCache[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const resolved: Record<string, string> = {};
      await Promise.all(
        missing.map(async (id) => {
          try {
            const response = await userService.getUserById(id);
            const user = response?.data || response;
            resolved[id] = displayName(user);
          } catch {
            resolved[id] = id;
          }
        })
      );
      if (!cancelled) setNameCache((prev) => ({ ...prev, ...resolved }));
    })();

    return () => {
      cancelled = true;
    };
    // nameCache is intentionally omitted so resolving does not retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value]);

  const toggleUser = (user_id: string) => {
    setSelectedIds((prev) =>
      prev.includes(user_id) ? prev.filter((id) => id !== user_id) : [...prev, user_id]
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Users"
      subtitle={`These users will be notified for ${eventLabel}`}
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {selectedIds.length} user{selectedIds.length === 1 ? '' : 's'} selected
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave(selectedIds);
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Apply Selection
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Only active users are listed */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search active users by name, email, or employee ID"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {selectedIds.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Selected
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedIds.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-full"
                >
                  <span className="font-medium">{nameCache[id] || 'Loading...'}</span>
                  <button
                    onClick={() => toggleUser(id)}
                    className="p-0.5 hover:bg-primary-100 rounded-full transition-colors"
                    aria-label="Remove user"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
                  <div className="flex-1 space-y-2">
                    <SkeletonLoader type="text" className="h-4 w-40" />
                    <SkeletonLoader type="text" className="h-3 w-56" />
                  </div>
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Users className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-0.5">No users found</p>
              <p className="text-xs text-gray-500 text-center max-w-xs">
                {debouncedSearch
                  ? 'Try a different search term.'
                  : 'There are no active users to select.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {users.map((user) => {
                const checked = selectedIds.includes(user._id);
                return (
                  <button
                    key={user._id}
                    onClick={() => toggleUser(user._id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <span
                      className={`w-5 h-5 rounded flex items-center justify-center border flex-shrink-0 ${
                        checked
                          ? 'bg-primary-600 border-primary-600'
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      {checked && <Check className="w-3.5 h-3.5 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {user.name}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">{user.email}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {total > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}

        {isLoading && users.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading users...
          </div>
        )}
      </div>
    </Modal>
  );
}

export default UserPickerModal;
